/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { ShowMoreGridSection } from '@coursera-web/cds-labs';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import {
  getDegreeProductType,
  getDegreeUrl,
  isSupportedDegreeVariant,
} from 'bundles/partners-graphql/components/cds/degreeUtils';
import type { PartnerDegreeFragment } from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerDegreesQuery';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';

import _t from 'i18n!nls/partners-graphql';

const styles = {
  sectionPadding: css`
    padding: var(--cds-spacing-300) 0;
  `,
};

type Props = {
  degrees: PartnerDegreeFragment[];
  title?: string;
};

const DegreeCardsSection = ({ degrees, title = _t('Degrees') }: Props) => {
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  if (degrees.length === 0) {
    return null;
  }

  const supportedDegrees = degrees.filter((d) => isSupportedDegreeVariant(d.productVariant));
  const degreeVariants = new Set(['BACHELORS_DEGREE', 'MASTERS_DEGREE']);
  const degreeItems = supportedDegrees
    .filter((d) => degreeVariants.has(d.productVariant))
    .sort((a, b) => a.name.localeCompare(b.name));
  const nonDegreeItems = supportedDegrees
    .filter((d) => !degreeVariants.has(d.productVariant))
    .sort((a, b) => a.name.localeCompare(b.name));
  const sortedDegrees = [...degreeItems, ...nonDegreeItems];

  const cardVariant = isMobile || sortedDegrees.length <= 2 ? 'list' : 'grid';

  const renderDegreeCard = ({ item: degree, itemIndex }: { item: PartnerDegreeFragment; itemIndex: number }) => {
    return (
      <LearningProductCard
        id={degree.id}
        slug={degree.slug}
        name={degree.name}
        linkProps={{ href: getDegreeUrl(degree.productVariant, degree.slug) }}
        variant={cardVariant}
        productType={getDegreeProductType(degree.productVariant)}
        partners={degree.partners.map((partner) => ({
          id: partner.id,
          name: partner.name,
          logoUrl: partner.squareLogo ?? undefined,
        }))}
        ImageComponent={
          degree.thumbnailUrl ? (
            <LearningProductCardImage imageUrl={degree.thumbnailUrl} variant={cardVariant} />
          ) : undefined
        }
        cardIndex={itemIndex}
      />
    );
  };

  return (
    <div data-e2e="partner-degrees-section">
      <ShowMoreGridSection
        css={styles.sectionPadding}
        title={title}
        items={sortedDegrees}
        renderItem={renderDegreeCard}
        itemsGridConfig={cardVariant === 'list' ? { xs: 12, sm: 6, md: 6, lg: 6 } : { xs: 12, sm: 6, md: 4, lg: 3 }}
        defaultRows={isMobile ? 2 : 1}
        showMoreAmount={8}
        spacing={24}
      />
    </div>
  );
};

export default DegreeCardsSection;
